import { useSearchParams } from 'react-router-dom'
import { compact, get } from 'lodash'
import moment from 'moment'
import { useEffect, useState, useMemo } from 'react'
import { useMiniSearch } from 'react-minisearch'
import { decodeFilters, FilterableColumn, FilterObject, FilterValueType } from 'app/filter.utils'

export default function useClientSideTableFilters<T>(
  rows: T[] | undefined,
  filterableColumns: FilterableColumn[],
  searchIdField: string,
  searchFields: string[],
  searchValue?: string,
): T[] | undefined {
  const miniSearchOptions = {
    idField: searchIdField,
    fields: searchFields,
    searchOptions: {
      prefix: true,
      combineWith: `AND` as const,
    },
    extractField: (document, fieldName) => {
      return fieldName.split(`.`).reduce((doc, key) => doc && doc[key], document)
    },
  }
  const addInfoToInitialFilters = (filters: FilterObject[]): FilterObject[] => {
    return filters?.map((filter) => ({
      ...filter,
      displayName:
        filterableColumns.find((col) => col[`attribute`] === filter.attribute)?.[`displayName`] ?? filter.attribute,
      type: filterableColumns.find((col) => col[`attribute`] === filter.attribute)?.type ?? FilterValueType.string,
    }))
  }
  const [filteredRows, setFilteredRows] = useState(rows)
  const [filteredAndSearchedRows, setFilteredAndSearchedRows] = useState(rows)
  const [searchParams] = useSearchParams()
  const filtersFromURL = useMemo(() => searchParams.get(`filters`), [searchParams])
  const joinFromURL = useMemo(() => searchParams.get(`join`) ?? `and`, [searchParams])
  const loadedFilters = useMemo(() => addInfoToInitialFilters(decodeFilters(filtersFromURL)), [filtersFromURL])
  const { search, addAll, removeAll, searchResults } = useMiniSearch(rows || [], miniSearchOptions)

  // when rows prop changes reset the search index and the filters
  useEffect(() => {
    setFilteredRows(rows)
    removeAll()
    if (rows?.length) {
      addAll(rows)
    }
  }, [rows])

  // when the rows prop or the loaded filters from the URL have changed, reapply the filters
  useEffect(() => {
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    applyFilters(joinFromURL as `or` | `and`)
  }, [loadedFilters, rows])

  // when the filtered rows change, reapply the search query on them. Also rerun the search query every time search value changes
  useEffect(() => {
    if (searchValue) {
      search(searchValue)
    } else {
      setFilteredAndSearchedRows(filteredRows)
    }
  }, [searchValue, filteredRows])

  useEffect(() => {
    if (searchResults?.length === 0) {
      setFilteredAndSearchedRows([])
    } else if (searchResults) {
      setFilteredAndSearchedRows(
        filteredRows?.filter(
          (row) => !!searchResults.find((result: any) => result[searchIdField] === row[searchIdField]),
        ),
      )
    } else {
      setFilteredAndSearchedRows(filteredRows)
    }
  }, [searchResults])

  /**
   * To join filters with OR we need to check if any of the filters are true
   * if so, row can be shown
   */
  const applyFilters = (join: `or` | `and`) => {
    const getCanShow = (row: T): boolean => {
      let canShow = join === `or` ? false : true

      for (const filter of loadedFilters) {
        if (
          typeof filter.value === `undefined` ||
          filter.value === `` ||
          (Array.isArray(filter.value) && compact(filter.value).length === 0)
        ) {
          continue
        }
        if (canShow === (join === `or`)) {
          break
        }
        switch (filter.type) {
          case FilterValueType.pmsIntegration:
            if (
              (filter.value === `false` &&
                (!get(row, filter.attribute) ||
                  get(row, filter.attribute).toString() === `false` ||
                  (get(row, filter.attribute).toString() === `true` &&
                    moment(get(row as any, `location.lastPmsSyncDate`)).isBefore(moment().subtract(2, `days`))))) ||
              (filter.value === `true` &&
                get(row, filter.attribute) &&
                get(row, filter.attribute).toString() === `true` &&
                moment(get(row as any, `location.lastPmsSyncDate`)).isAfter(moment().subtract(2, `days`)))
            ) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.boolean:
            if (get(row, filter.attribute)?.toString() === filter.value) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.attributeExists:
            if (get(row, filter.attribute) === (filter.value === `true`)) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.rangeDate:
          case FilterValueType.number:
          case FilterValueType.age:
            if (
              (filter.operator === `exactly` && get(row, filter.attribute) === +filter.value) ||
              (filter.operator === `greater_than` && get(row, filter.attribute) >= +filter.value) ||
              (filter.operator === `lesser_than` && get(row, filter.attribute) <= +filter.value) ||
              (filter.operator === `between` &&
                (get(row, filter.attribute) >= +filter.value?.[0] || get(row, filter.attribute) <= +filter.value?.[1]))
            ) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.id:
          case FilterValueType.stringArray:
          case FilterValueType.multiple:
            if (
              get(row, filter.attribute) &&
              ((filter.operator === `in` &&
                (filter.value as string[]).some((value) => (get(row, filter.attribute) as string[]).includes(value))) ||
                (filter.operator === `not_in` &&
                  !(filter.value as string[]).some((value) =>
                    (get(row, filter.attribute) as string[]).includes(value),
                  )))
            ) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.string:
            if (
              (filter.operator === `exactly` &&
                get(row, filter.attribute).toLowerCase() !== (filter.value as string).toLowerCase()) ||
              (filter.operator === `contains` &&
                get(row, filter.attribute)
                  .toLowerCase()
                  .includes((filter.value as string).toLowerCase())) ||
              (filter.operator === `not_contains` &&
                !get(row, filter.attribute)
                  .toLowerCase()
                  .includes((filter.value as string).toLowerCase()))
            ) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
            break
          case FilterValueType.assignedTo:
            if (!!filter.value && filter.value.includes(get(row, filter.attribute) as string)) {
              canShow = true
            } else if (join === `and`) {
              canShow = false
            }
        }
      }
      return canShow
    }
    setFilteredRows(rows?.filter((row) => getCanShow(row)))
  }

  return filteredAndSearchedRows
}
